/**
 * LO Prospect Pipeline (Partner Pricing Preview) — types, DB row shapes,
 * snake<->camel mappers, and derived selectors.
 *
 * A "prospect" is a hand-picked loan officer being courted to join LendWise.
 * They run up to 5 OB pricing scenarios (lo_prospect_pricing_runs); recruiters
 * work them by stage and transfer them into the `recruits` table.
 *
 * Namespaced lo* to avoid collision with the Encompass loan-prospect domain.
 */

import { displayObInvestorLabel, displayObProductLabels } from '@/utils/pricing/obProductLabels';

// ============================================================================
// Stages
// ============================================================================

export type LoProspectStage =
  | 'registered'
  | 'invited'
  | 'previewing'
  | 'in_recruiting'
  | 'passed';

/** How the prospect entered the pipeline (chk_lo_prospects_source). */
export type LoProspectSource = 'recruiter_invite' | 'self_registration';

/** Self-registration review status (chk_lo_prospects_registration_status). */
export type LoProspectRegistrationStatus = 'pending_review' | 'approved' | 'rejected';

export interface StageMeta {
  label: string;
  /** CSS class consumed by the shared .lw-ds design-kit theme */
  badgeClass: string;
}

export const LO_STAGE_META: Record<LoProspectStage, StageMeta> = {
  registered: { label: 'Registered', badgeClass: 'badge--registered' },
  invited: { label: 'Invited', badgeClass: 'badge--invited' },
  previewing: { label: 'Previewing', badgeClass: 'badge--previewing' },
  in_recruiting: { label: 'In Recruiting', badgeClass: 'badge--committed' },
  passed: { label: 'Passed', badgeClass: 'badge--passed' }
};

export const LO_STAGE_ORDER: LoProspectStage[] = [
  'registered',
  'invited',
  'previewing',
  'in_recruiting',
  'passed'
];

/**
 * The live DB CHECK still permits the retired 'ready_for_recruiting' value
 * (kept as a constraint superset; no flow writes it, but authenticated table
 * access could still produce one). Normalize any legacy value at the data
 * boundary so the UI never sees a stage outside LoProspectStage.
 */
export function normalizeLoProspectStage(stage: string): LoProspectStage {
  return stage === 'ready_for_recruiting' ? 'previewing' : (stage as LoProspectStage);
}

/** Positions allowed on a prospect (wider than RecruitPosition). */
export const LO_PROSPECT_POSITIONS = [
  'Loan Officer',
  'Senior Loan Officer',
  'Branch Manager',
  'Mortgage Loan Originator'
] as const;

export const MAX_PREVIEW_RUNS = 5;

// ============================================================================
// Frontend types
// ============================================================================

export type LoProspectEngagementType = 'pricing_run' | 'invite_opened' | 'nmls_login';

export interface LoProspectEngagementSummary {
  lastInviteOpenedAt: string | null;
  inviteOpenedCount: number;
  lastNmlsLoginAt: string | null;
  nmlsLoginCount: number;
}

export type LoProspectPricingFailureCategory =
  | 'scenario_validation'
  | 'upstream_auth'
  | 'upstream_rate_limit'
  | 'timeout'
  | 'network'
  | 'upstream_unavailable'
  | 'invalid_response'
  | 'legacy';

export interface LoProspectPricingFailureIssue {
  input: string | null;
  message: string;
}

export interface LoProspectPricingFailure {
  id: string;
  attemptedAt: string;
  category: LoProspectPricingFailureCategory;
  httpStatus: number | null;
  message: string;
  issues: LoProspectPricingFailureIssue[];
  retryable: boolean;
  traceId: string | null;
  scenario: Record<string, unknown>;
  legacy: boolean;
}

export interface LoProspectDerivedEngagement {
  lastEngagementAt: string | null;
  lastEngagementType: LoProspectEngagementType | null;
}

export interface InvestorResult {
  investor: string;
  productName: string;
  displayInvestor: string;
  displayProductName: string;
  rate: number; // percent, e.g. 6.49
  price: number; // points, e.g. 101.070
  payment: number; // monthly P&I, dollars
  closingCost: number; // dollars (display only; not a ranking key)
  makePercent: number; // LO gross premium = price - 100
  makeDollars: number; // makePercent% of the loan amount
  qualified: boolean; // price funds the selected comp (>= desired floor)
}

export interface PricingScenario {
  id: string;
  round: number;
  isCurrent: boolean;
  type: string; // loan purpose: 'Purchase' | 'Refinance' | 'Cash-out'
  product: string;
  amount: number; // loan amount
  fico: number | null;
  ltv: number | null;
  term: number | null;
  lock: number | null; // lock days
  ranAt: string | null; // created_at ISO
  results: InvestorResult[];
  bestRate: number | null;
  bestPrice: number | null;
  bestInvestor: string | null;
  displayBestInvestor: string | null;
  compPercent: number | null; // selected "how much do you want to make" %
  desiredPrice: number | null; // 100 + compPercent (the qualify floor)
  compFunded: boolean; // at least one product reaches the floor
  bestMakePercent: number | null;
  bestMakeDollars: number | null;
  // Pricing-profile snapshot (which OB seat priced this run). Null on legacy
  // rows that predate the profiles migration.
  pricingProfileLabel: string | null;
  pricingProfileResolutionReason: string | null;
}

export interface HistoryRound {
  round: number;
  archivedAt: string | null;
  scenarios: PricingScenario[];
}

export interface LoProspect {
  id: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  nmls: string;
  dre: string | null;
  email: string | null;
  phone: string | null;
  position: string | null;
  yearsExperience: number | null;
  city: string | null;
  state: string | null;
  licenses: string[];
  stage: LoProspectStage;
  publicPreviewEnabled: boolean;
  previewInviteUnsubscribedAt: string | null;
  source: LoProspectSource;
  registrationStatus: LoProspectRegistrationStatus | null;
  recruiterId: string | null;
  pricingProfileId: string | null; // null = follow the org default pricing profile
  notes: string;
  lastActiveAt: string | null;
  lastInviteOpenedAt: string | null;
  inviteOpenedCount: number;
  lastNmlsLoginAt: string | null;
  nmlsLoginCount: number;
  lastEngagementAt: string | null;
  lastEngagementType: LoProspectEngagementType | null;
  transitionedRecruitId: string | null;
  createdAt: string;
  updatedAt: string;

  // Joined pricing
  scenarios: PricingScenario[]; // current batch (is_current = true)
  history: HistoryRound[]; // archived rounds (is_current = false), newest first

  // Derived (compute, don't store)
  runsUsed: number; // = scenarios.length (0..MAX_PREVIEW_RUNS)
  bestRate: number | null;
  bestPrice: number | null;
  bestInvestor: string | null;
  displayBestInvestor: string | null;
}

// ============================================================================
// Database row shapes (snake_case)
// ============================================================================

export interface LoProspectRow {
  id: string;
  organization_id: string;
  first_name: string | null;
  last_name: string | null;
  nmls: string;
  dre: string | null;
  email: string | null;
  phone: string | null;
  position: string | null;
  years_experience: number | null;
  city: string | null;
  state: string | null;
  licenses: string[] | null;
  stage: LoProspectStage;
  recruiter_id: string | null;
  pricing_profile_id?: string | null; // optional: absent until the profiles migration lands
  notes: string | null;
  last_active_at: string | null;
  transitioned_recruit_id: string | null;
  public_preview_enabled: boolean | null;
  preview_invite_unsubscribed_at: string | null;
  source?: LoProspectSource | null;
  registration_status?: LoProspectRegistrationStatus | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
}

/** Subset of an OB product row we surface as an InvestorResult. */
export interface ObEligibleProductLike {
  investor?: string | null;
  productName?: string | null;
  rate?: number | null;
  price?: number | null;
  principalAndInterest?: number | null;
  closingCost?: number | null;
  loCompensationPercent?: number | null;
  loCompensationDollars?: number | null;
  compBps?: number | null;
}

export interface LoProspectPricingRunRow {
  id: string;
  lo_prospect_id: string;
  organization_id: string;
  round: number;
  is_current: boolean;
  archived_at: string | null;
  loan_purpose: string | null;
  loan_type: string | null;
  product: string | null;
  loan_amount: number | null;
  fico: number | null;
  ltv: number | null;
  term: number | null;
  lock_days: number | null;
  ob_search_id: string | null;
  ob_request_payload_redacted: unknown | null;
  ob_response_summary: unknown | null;
  ob_eligible_products: ObEligibleProductLike[] | null;
  ob_ineligible_summary: unknown | null;
  best_investor: string | null;
  best_rate: number | null;
  best_price: number | null;
  best_monthly_pi: number | null;
  best_lo_comp_percent: number | null;
  best_lo_comp_dollars: number | null;
  comp_percent: number | null;
  desired_price: number | null;
  comp_funded: boolean | null;
  note_rate: number | null;
  status: string; // 'priced' | 'no_products' | 'ob_error'
  error_message: string | null;
  created_at: string;
  // Pricing-profile snapshot columns (absent until the profiles migration lands).
  pricing_profile_id?: string | null;
  pricing_profile_label?: string | null;
  pricing_profile_resolution_reason?: string | null;
}

/** Insert payload for inviting a prospect. */
export interface LoProspectInsert {
  organization_id: string;
  nmls: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  position: string | null;
  recruiter_id: string | null;
  /** Only include when a non-null profile was deliberately chosen — omitting the
   *  key keeps invites working before the profiles migration lands (PostgREST
   *  rejects unknown columns) and lets the column default carry NULL after. */
  pricing_profile_id?: string | null;
  stage: LoProspectStage;
  created_by: string | null;
  updated_by: string | null;
}

// ============================================================================
// Mappers: DB -> frontend
// ============================================================================

const round2 = (n: number): number => Math.round(n * 1000) / 1000;

const ENGAGEMENT_PRIORITY: Record<LoProspectEngagementType, number> = {
  pricing_run: 3,
  nmls_login: 2,
  invite_opened: 1
};

function timeValue(iso: string | null | undefined): number | null {
  if (!iso) {
    return null;
  }
  const time = Date.parse(iso);
  return Number.isFinite(time) ? time : null;
}

export function deriveLoProspectEngagement(input: {
  lastActiveAt?: string | null;
  lastInviteOpenedAt?: string | null;
  lastNmlsLoginAt?: string | null;
}): LoProspectDerivedEngagement {
  const candidates: Array<{ type: LoProspectEngagementType; at: string; time: number }> = [];
  const pricingTime = timeValue(input.lastActiveAt);
  const inviteTime = timeValue(input.lastInviteOpenedAt);
  const nmlsTime = timeValue(input.lastNmlsLoginAt);

  if (pricingTime !== null && input.lastActiveAt) {
    candidates.push({ type: 'pricing_run', at: input.lastActiveAt, time: pricingTime });
  }
  if (nmlsTime !== null && input.lastNmlsLoginAt) {
    candidates.push({ type: 'nmls_login', at: input.lastNmlsLoginAt, time: nmlsTime });
  }
  if (inviteTime !== null && input.lastInviteOpenedAt) {
    candidates.push({ type: 'invite_opened', at: input.lastInviteOpenedAt, time: inviteTime });
  }

  const latest = candidates.reduce<typeof candidates[number] | null>((best, candidate) => {
    if (!best) {
      return candidate;
    }
    if (candidate.time > best.time) {
      return candidate;
    }
    if (candidate.time === best.time && ENGAGEMENT_PRIORITY[candidate.type] > ENGAGEMENT_PRIORITY[best.type]) {
      return candidate;
    }
    return best;
  }, null);

  return {
    lastEngagementAt: latest?.at ?? null,
    lastEngagementType: latest?.type ?? null
  };
}

/** Qualify tolerance — mirrors QUALIFY_EPS in the lo-prospect-price edge fn. */
export const QUALIFY_EPS = 0.005;

/**
 * Derive the investor ladder from a run's stored OB products, mirroring the
 * edge function's selectBest comparator. `desiredPrice` is the comp floor
 * (100 + comp%); a product at/above it "funds" the comp and is `qualified`.
 * Qualified rows sort first (lowest rate = best), below-target rows follow.
 * Legacy runs (no `desiredPrice`) qualify nothing (and get no Best badge).
 * True-identical rows (same investor+product+rate+price) are de-duplicated.
 */
export function resultsFromProducts(
  products: ObEligibleProductLike[] | null | undefined,
  desiredPrice?: number | null,
  loanAmount?: number | null
): InvestorResult[] {
  if (!Array.isArray(products)) {
    return [];
  }
  const floor = typeof desiredPrice === 'number' && desiredPrice > 0 ? desiredPrice : null;
  const amount = typeof loanAmount === 'number' ? loanAmount : 0;

  const seen = new Set<string>();
  const rows: InvestorResult[] = [];
  for (const p of products) {
    if (!p || typeof p.rate !== 'number') {
      continue;
    }
    const investor = p.investor ?? 'Investor';
    const productName = p.productName ?? '';
    const rate = round2(Number(p.rate));
    const price = typeof p.price === 'number' ? round2(p.price) : 0;
    const key = `${investor}|${productName}|${rate}|${price}`;
    if (seen.has(key)) {
      continue; // collapse only true-identical rows (distinct products are kept)
    }
    seen.add(key);
    const makePercent = round2(price - 100);
    const display = displayObProductLabels(investor, productName);
    rows.push({
      investor,
      productName,
      displayInvestor: display.displayInvestor,
      displayProductName: display.displayProductName,
      rate,
      price,
      payment: typeof p.principalAndInterest === 'number' ? Math.round(p.principalAndInterest) : 0,
      closingCost: typeof p.closingCost === 'number' ? Math.round(p.closingCost) : 0,
      makePercent,
      makeDollars: Math.round((makePercent / 100) * amount),
      qualified: floor !== null && price + QUALIFY_EPS >= floor
    });
  }
  // Qualified first; within each group lowest rate, then highest price, then product.
  rows.sort(
    (a, b) =>
      Number(b.qualified) - Number(a.qualified) ||
      a.rate - b.rate ||
      b.price - a.price ||
      a.productName.localeCompare(b.productName)
  );
  return rows;
}

export function scenarioFromRunRow(row: LoProspectPricingRunRow): PricingScenario {
  const results = resultsFromProducts(row.ob_eligible_products, row.desired_price, row.loan_amount);
  const productsPresent = Array.isArray(row.ob_eligible_products) && row.ob_eligible_products.length > 0;
  // Re-derive best from products + the persisted floor (don't trust stale best_*);
  // best = the first qualified row. Fall back to stored best_* only when the run
  // has no stored products (shouldn't happen for a priced run).
  const best = results.find((r) => r.qualified) ?? null;
  return {
    id: row.id,
    round: row.round,
    isCurrent: row.is_current,
    type: row.loan_purpose ?? 'Scenario',
    product: row.product ?? row.loan_type ?? '',
    amount: typeof row.loan_amount === 'number' ? row.loan_amount : 0,
    fico: row.fico,
    ltv: row.ltv,
    term: row.term,
    lock: row.lock_days,
    ranAt: row.created_at,
    results,
    bestRate: productsPresent ? (best ? best.rate : null) : row.best_rate,
    bestPrice: productsPresent ? (best ? best.price : null) : row.best_price,
    bestInvestor: productsPresent ? (best ? best.investor : null) : row.best_investor,
    displayBestInvestor: productsPresent
      ? (best ? best.displayInvestor : null)
      : row.best_investor
        ? displayObInvestorLabel(row.best_investor) || row.best_investor.trim() || null
        : null,
    compPercent: row.comp_percent ?? null,
    desiredPrice: row.desired_price ?? null,
    compFunded: best !== null,
    bestMakePercent: best ? best.makePercent : null,
    bestMakeDollars: best ? best.makeDollars : null,
    pricingProfileLabel: row.pricing_profile_label ?? null,
    pricingProfileResolutionReason: row.pricing_profile_resolution_reason ?? null
  };
}

/**
 * Build a full LoProspect from its row + its pricing-run rows.
 * Current rows (is_current) become `scenarios`; archived rows group into
 * `history` rounds (newest round first). Derived fields come from the current
 * batch's investor results (lowest rate surfaced).
 */
export function loProspectFromRows(
  row: LoProspectRow,
  runRows: LoProspectPricingRunRow[] = [],
  engagement: Partial<LoProspectEngagementSummary> = {}
): LoProspect {
  const current: PricingScenario[] = [];
  const archivedByRound = new Map<number, { archivedAt: string | null; scenarios: PricingScenario[] }>();

  for (const r of runRows) {
    // Audit-only OB failure rows are never surfaced as scenarios.
    if (r.status === 'ob_error') {
      continue;
    }
    const scn = scenarioFromRunRow(r);
    if (r.is_current) {
      current.push(scn);
    } else {
      const bucket = archivedByRound.get(r.round) ?? { archivedAt: r.archived_at, scenarios: [] };
      bucket.scenarios.push(scn);
      if (r.archived_at) {
        bucket.archivedAt = r.archived_at;
      }
      archivedByRound.set(r.round, bucket);
    }
  }

  const history: HistoryRound[] = [...archivedByRound.entries()]
    .map(([roundNum, b]) => ({ round: roundNum, archivedAt: b.archivedAt, scenarios: b.scenarios }))
    .sort((a, b) => b.round - a.round);

  // Prospect headline best = lowest-rate quote that funds the comp (qualified),
  // across the current batch. Below-target quotes never become the headline.
  let best: InvestorResult | null = null;
  for (const s of current) {
    for (const res of s.results) {
      if (!res.qualified) {
        continue;
      }
      if (!best || res.rate < best.rate || (res.rate === best.rate && res.price > best.price)) {
        best = res;
      }
    }
  }

  const lastInviteOpenedAt = engagement.lastInviteOpenedAt ?? null;
  const inviteOpenedCount = engagement.inviteOpenedCount ?? 0;
  const lastNmlsLoginAt = engagement.lastNmlsLoginAt ?? null;
  const nmlsLoginCount = engagement.nmlsLoginCount ?? 0;
  const derivedEngagement = deriveLoProspectEngagement({
    lastActiveAt: row.last_active_at,
    lastInviteOpenedAt,
    lastNmlsLoginAt
  });

  return {
    id: row.id,
    organizationId: row.organization_id,
    firstName: row.first_name ?? '',
    lastName: row.last_name ?? '',
    nmls: row.nmls,
    dre: row.dre,
    email: row.email,
    phone: row.phone,
    position: row.position,
    yearsExperience: row.years_experience,
    city: row.city,
    state: row.state,
    licenses: row.licenses ?? [],
    stage: normalizeLoProspectStage(row.stage),
    publicPreviewEnabled: row.public_preview_enabled ?? false,
    previewInviteUnsubscribedAt: row.preview_invite_unsubscribed_at ?? null,
    source: row.source ?? 'recruiter_invite',
    registrationStatus: row.registration_status ?? null,
    recruiterId: row.recruiter_id,
    pricingProfileId: row.pricing_profile_id ?? null,
    notes: row.notes ?? '',
    lastActiveAt: row.last_active_at,
    lastInviteOpenedAt,
    inviteOpenedCount,
    lastNmlsLoginAt,
    nmlsLoginCount,
    lastEngagementAt: derivedEngagement.lastEngagementAt,
    lastEngagementType: derivedEngagement.lastEngagementType,
    transitionedRecruitId: row.transitioned_recruit_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    scenarios: current,
    history,
    runsUsed: current.length,
    bestRate: best ? best.rate : null,
    bestPrice: best ? best.price : null,
    bestInvestor: best ? best.investor : null,
    displayBestInvestor: best ? best.displayInvestor : null
  };
}

// ============================================================================
// Mappers: frontend -> DB update (camel -> snake; only provided keys)
// ============================================================================

export interface LoProspectEditableFields {
  firstName: string;
  lastName: string;
  nmls: string;
  dre: string | null;
  email: string | null;
  phone: string | null;
  position: string | null;
  yearsExperience: number | null;
  city: string | null;
  state: string | null;
  licenses: string[];
  recruiterId: string | null;
  /** Optional on purpose: absent = "don't touch the assignment" (the update
   *  OMITS the column — required while the frontend can deploy before the
   *  profiles migration). Present null = explicitly follow the org default. */
  pricingProfileId?: string | null;
  notes: string;
}

export function loProspectUpdateToRow(
  patch: Partial<LoProspectEditableFields>,
  userId?: string
): Record<string, unknown> {
  const out: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (userId) {
    out.updated_by = userId;
  }
  if (patch.firstName !== undefined) {
out.first_name = patch.firstName;
}
  if (patch.lastName !== undefined) {
out.last_name = patch.lastName;
}
  if (patch.nmls !== undefined) {
out.nmls = patch.nmls;
}
  if (patch.dre !== undefined) {
out.dre = patch.dre || null;
}
  if (patch.email !== undefined) {
out.email = patch.email || null;
}
  if (patch.phone !== undefined) {
out.phone = patch.phone || null;
}
  if (patch.position !== undefined) {
out.position = patch.position || null;
}
  if (patch.yearsExperience !== undefined) {
out.years_experience = patch.yearsExperience;
}
  if (patch.city !== undefined) {
out.city = patch.city || null;
}
  if (patch.state !== undefined) {
out.state = patch.state || null;
}
  if (patch.licenses !== undefined) {
out.licenses = patch.licenses;
}
  if (patch.recruiterId !== undefined) {
out.recruiter_id = patch.recruiterId || null;
}
  if (patch.pricingProfileId !== undefined) {
out.pricing_profile_id = patch.pricingProfileId || null;
}
  if (patch.notes !== undefined) {
out.notes = patch.notes ?? '';
}
  return out;
}

// ============================================================================
// Derived selectors (ported from the prototype's data.jsx / App())
// ============================================================================

export type RunsBucket = 'none' | 'progress' | 'full';

export function runsBucket(p: Pick<LoProspect, 'runsUsed'>): RunsBucket {
  if (p.runsUsed === 0) {
return 'none';
}
  return p.runsUsed >= MAX_PREVIEW_RUNS ? 'full' : 'progress';
}

export const RUNS_BUCKET_LABEL: Record<RunsBucket, string> = {
  none: 'Not started',
  progress: 'In progress',
  full: `Complete · ${MAX_PREVIEW_RUNS} of ${MAX_PREVIEW_RUNS}`
};

export interface FacetCounts {
  stage: Record<string, number>;
  state: Record<string, number>;
  recruiter: Record<string, number>;
  runs: Record<RunsBucket, number>;
}

export function facetCounts(prospects: LoProspect[]): FacetCounts {
  const c: FacetCounts = {
    stage: {},
    state: {},
    recruiter: {},
    runs: { none: 0, progress: 0, full: 0 }
  };
  for (const p of prospects) {
    c.stage[p.stage] = (c.stage[p.stage] || 0) + 1;
    for (const s of p.licenses) {
      c.state[s] = (c.state[s] || 0) + 1;
    }
    const rk = p.recruiterId || 'unassigned';
    c.recruiter[rk] = (c.recruiter[rk] || 0) + 1;
    const rb = runsBucket(p);
    c.runs[rb] += 1;
  }
  return c;
}

/** Stats for the funnel KPI: total runs used across the loaded list. */
export function pipelineStats(prospects: LoProspect[]): {
  runs: number;
  runsCap: number;
  pricedCount: number;
  total: number;
} {
  const runs = prospects.reduce((sum, p) => sum + p.runsUsed, 0);
  return {
    runs,
    runsCap: prospects.length * MAX_PREVIEW_RUNS,
    pricedCount: prospects.filter((p) => p.runsUsed > 0).length,
    total: prospects.length
  };
}
